/**
 * useOrbitChat
 *
 * Manages a complete Orbit chat conversation with streaming support.
 *
 * - Appends user messages immediately
 * - Creates a streaming assistant message in real-time as tokens arrive
 * - Tracks conversationId for multi-turn sessions
 * - Cancels in-flight requests on component unmount or new message
 *
 * Usage:
 *   const { messages, isStreaming, sendMessage, resetConversation } =
 *     useOrbitChat({ clientId: 123, client: { Name: 'Acme', ClientCode: '1234' } });
 */
import { useState, useRef, useCallback, useEffect } from 'react';

import {
  streamOrbitChat,
  submitOrbitFeedback,
  fetchOrbitConversationMessages,
  OrbitClient,
  OrbitDoneEvent,
  OrbitErrorEvent,
  OrbitFeedbackPayload,
  OrbitContextChips,
  OrbitConversationMessage,
  OrbitChatUpload,
} from '../services/OrbitChatService';
import type { ChatMessage, OrbitExportTables } from '../components/orbit/indie-chat.types';
import { describeOrbitAttachment, mergeOrbitContextUploads } from '../services/OrbitChatUploadUtils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseOrbitChatOptions {
  clientId:              number;
  client:                OrbitClient;
  /** If provided, load this conversation from history and resume it */
  initialConversationId?: string;
  /** Optional flyout → full-screen handoff state for pending responses */
  initialHandoff?: OrbitChatViewHandoff;
}

export interface OrbitChatViewHandoff {
  conversationId?: string;
  messages?: ChatMessage[];
  isStreaming?: boolean;
  streamingMessageId?: string | null;
  contextChips?: OrbitContextChips;
  contextUploads?: OrbitChatUpload[];
}

export interface UseOrbitChatResult {
  messages:           ChatMessage[];
  isStreaming:        boolean;
  streamingMessageId: string | null;
  quotaExceeded:      boolean;
  /** Stable conversation UUID — generated client-side on first message, reset on resetConversation */
  conversationId:     string | undefined;
  /** True while loading a historic conversation thread */
  isLoadingHistory:   boolean;
  /** Current context filter chips (period / department / location) */
  contextChips:       OrbitContextChips;
  setContextChips:    (chips: OrbitContextChips) => void;
  contextUploads:     OrbitChatUpload[];
  addContextUploads:  (uploads: OrbitChatUpload[]) => void;
  removeContextUpload:(index: number) => void;
  sendMessage:        (text: string, uploads?: OrbitChatUpload[]) => void;
  resetConversation:  () => void;
  submitFeedback:     (payload: OrbitFeedbackPayload) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function makeId(): string {
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function userMsg(content: string, uploads: OrbitChatUpload[] = []): ChatMessage {
  const attachments = uploads.map((upload) => describeOrbitAttachment(upload));
  return {
    id: makeId(),
    role: 'user',
    content,
    metadata: attachments.length > 0 ? { attachments } : undefined,
  };
}

function assistantMsg(content = ''): ChatMessage {
  return { id: makeId(), role: 'assistant', content };
}

function mapConversationMessages(apiMessages: OrbitConversationMessage[]): ChatMessage[] {
  return apiMessages
    .filter((m) => m.message_role === 'user' || m.message_role === 'assistant')
    .map((m) => {
      const exportTables = historyExportTables((m as { content_json?: unknown }).content_json);
      return {
        id: `hist-${m.message_id}`,
        role: m.message_role as 'user' | 'assistant',
        content: m.content_text,
        ...(exportTables ? { export_tables: exportTables } : {}),
      };
    });
}

/** content_json may arrive as a JSON string depending on the DB driver. */
function historyExportTables(contentJson: unknown): OrbitExportTables | undefined {
  let parsed: unknown = contentJson;
  if (typeof contentJson === 'string') {
    try { parsed = JSON.parse(contentJson); } catch { return undefined; }
  }
  const tables = (parsed as { export_tables?: OrbitExportTables } | null)?.export_tables;
  return tables && Array.isArray(tables.tables) ? tables : undefined;
}

function hasCompletedAssistantMessage(chatMessages: ChatMessage[]): boolean {
  return chatMessages.some((message) => message.role === 'assistant' && message.content.trim().length > 0);
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useOrbitChat({ clientId, client, initialConversationId, initialHandoff }: UseOrbitChatOptions): UseOrbitChatResult {
  const handoffConversationId = initialHandoff?.conversationId;
  const resolvedInitialConversationId = initialConversationId ?? handoffConversationId;
  const handoffMatchesConversation = Boolean(
    resolvedInitialConversationId
    && handoffConversationId
    && handoffConversationId === resolvedInitialConversationId,
  );
  const handoffMessages = handoffMatchesConversation && Array.isArray(initialHandoff?.messages)
    ? initialHandoff?.messages ?? []
    : [];
  const initialMessages = handoffMatchesConversation
    ? handoffMessages
    : [];
  const [messages,           setMessages]           = useState<ChatMessage[]>(initialMessages);
  const [isStreaming,        setIsStreaming]         = useState(Boolean(handoffMatchesConversation && initialHandoff?.isStreaming));
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    handoffMatchesConversation ? initialHandoff?.streamingMessageId ?? null : null,
  );
  const [quotaExceeded,      setQuotaExceeded]      = useState(false);
  const [conversationId,     setConversationId]     = useState<string | undefined>(resolvedInitialConversationId);
  const [isLoadingHistory,   setIsLoadingHistory]   = useState(false);
  const [contextChips,       setContextChips]       = useState<OrbitContextChips>(
    handoffMatchesConversation ? initialHandoff?.contextChips ?? {} : {},
  );
  const [contextUploads,     setContextUploads]     = useState<OrbitChatUpload[]>(
    handoffMatchesConversation ? initialHandoff?.contextUploads ?? [] : [],
  );

  // Persistent across renders — not state
  const conversationIdRef = useRef<string | undefined>(resolvedInitialConversationId);
  const abortRef          = useRef<ReturnType<typeof streamOrbitChat> | null>(null);
  const appliedHandoffConversationRef = useRef<string | undefined>(
    handoffMatchesConversation ? resolvedInitialConversationId : undefined,
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!handoffMatchesConversation) {
      appliedHandoffConversationRef.current = undefined;
    }
  }, [handoffMatchesConversation]);

  useEffect(() => {
    if (!resolvedInitialConversationId || !handoffMatchesConversation) {
      return;
    }

    if (appliedHandoffConversationRef.current === resolvedInitialConversationId) {
      return;
    }

    appliedHandoffConversationRef.current = resolvedInitialConversationId;
    conversationIdRef.current = resolvedInitialConversationId;
    setConversationId(resolvedInitialConversationId);
    setMessages(handoffMessages);
    setIsStreaming(Boolean(initialHandoff?.isStreaming));
    setStreamingMessageId(initialHandoff?.streamingMessageId ?? null);
    setContextChips(initialHandoff?.contextChips ?? {});
    setContextUploads(initialHandoff?.contextUploads ?? []);
  }, [
    handoffMatchesConversation,
    initialHandoff?.contextChips,
    initialHandoff?.contextUploads,
    initialHandoff?.isStreaming,
    initialHandoff?.streamingMessageId,
    resolvedInitialConversationId,
  ]);

  // Load conversation from history when initialConversationId is provided
  useEffect(() => {
    if (!resolvedInitialConversationId || !clientId) return;

    const isHydratedPendingHandoff = Boolean(
      handoffMatchesConversation
      && isStreaming
      && !abortRef.current,
    );

    const isActiveConversationAlreadyLoaded = (
      resolvedInitialConversationId === conversationIdRef.current
      && !isHydratedPendingHandoff
      && ((Boolean(abortRef.current) && isStreaming) || messages.length > 0)
    );

    if (isActiveConversationAlreadyLoaded) {
      return;
    }

    let cancelled = false;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;

    if (!isHydratedPendingHandoff) {
      setMessages([]);          // clear previous messages immediately to avoid flash
      setContextUploads([]);
      setContextChips({});
      setIsLoadingHistory(true);
    }

    const loadHistory = () => {
      fetchOrbitConversationMessages(clientId, resolvedInitialConversationId).then(
        (apiMessages: OrbitConversationMessage[]) => {
          if (cancelled) return;
          const chatMessages = mapConversationMessages(apiMessages);
          if (chatMessages.length > 0 || !isHydratedPendingHandoff) {
            setMessages(chatMessages);
          }
          conversationIdRef.current = resolvedInitialConversationId;
          setConversationId(resolvedInitialConversationId);

          const shouldContinuePolling = isHydratedPendingHandoff && !hasCompletedAssistantMessage(chatMessages);
          if (shouldContinuePolling) {
            attempts += 1;
            retryTimeout = setTimeout(loadHistory, Math.min(4000, 250 * (attempts + 1)));
            return;
          }

          if (isHydratedPendingHandoff) {
            setIsStreaming(false);
            setStreamingMessageId(null);
          }
          setIsLoadingHistory(false);
        },
      ).catch(() => {
        if (cancelled) return;
        if (isHydratedPendingHandoff) {
          attempts += 1;
          retryTimeout = setTimeout(loadHistory, Math.min(4000, 250 * (attempts + 1)));
          return;
        }
        setIsLoadingHistory(false);
      });
    };

    loadHistory();

    return () => {
      cancelled = true;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [
    clientId,
    handoffMatchesConversation,
    isStreaming,
    messages.length,
    resolvedInitialConversationId,
  ]);

  const addContextUploads = useCallback((uploads: OrbitChatUpload[]) => {
    if (!Array.isArray(uploads) || uploads.length === 0) return;
    setContextUploads((prev) => mergeOrbitContextUploads(prev, uploads));
  }, []);

  const removeContextUpload = useCallback((index: number) => {
    setContextUploads((prev) => prev.filter((_, uploadIndex) => uploadIndex !== index));
  }, []);

  const sendMessage = useCallback(
    (text: string, uploads: OrbitChatUpload[] = []) => {
      // Cancel any in-flight request
      abortRef.current?.abort();
      setQuotaExceeded(false);

      const effectiveUploads = mergeOrbitContextUploads(contextUploads, uploads);

      // Assign conversation_id on the first message of each conversation
      if (!conversationIdRef.current) {
        const newId = typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `conv-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        conversationIdRef.current = newId;
        setConversationId(newId);
      }

      const userMessage = userMsg(text, effectiveUploads);
      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);

      // Reserve a placeholder for the streaming assistant reply
      const assistantPlaceholder = assistantMsg();
      setStreamingMessageId(assistantPlaceholder.id);
      setMessages((prev) => [...prev, assistantPlaceholder]);

      const idempotencyKey = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      abortRef.current = streamOrbitChat(
        clientId,
        client,
        {
          message:          text,
          conversation_id:  conversationIdRef.current,
          idempotency_key:  idempotencyKey,
          uploads: effectiveUploads,
          // Phase 2: forward active context chips to the backend
          context: {
            ...(contextChips.period     ? { period:     contextChips.period }     : {}),
            ...(contextChips.department ? { department: contextChips.department } : {}),
            ...(contextChips.location   ? { location:   contextChips.location }   : {}),
          },
        },
        {
          onToken: (token: string) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantPlaceholder.id
                  ? { ...m, content: m.content + (m.content ? ' ' : '') + token }
                  : m,
              ),
            );
          },

          onDone: (e: OrbitDoneEvent & { export_tables?: OrbitExportTables }) => {
            // Replace the assembled tokens with final_text if provided (avoids spacing quirks)
            if (e.final_text || e.export_tables) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantPlaceholder.id
                    ? {
                      ...m,
                      ...(e.final_text ? { content: e.final_text } : {}),
                      ...(e.export_tables ? { export_tables: e.export_tables } : {}),
                    }
                    : m,
                ),
              );
            }
          },

          onError: (e: OrbitErrorEvent) => {
            // QUOTA_EXCEEDED is a special non-dismissible banner
            if (e.error_code === 'QUOTA_EXCEEDED') {
              setQuotaExceeded(true);
              setMessages((prev) =>
                prev.filter((m) => m.id !== assistantPlaceholder.id),
              );
              return;
            }

            const errorText =
              typeof e.detail === 'string'
                ? e.detail
                : e.message || 'Something went wrong. Please try again.';
            const orbitErrorCode = typeof e.error_code === 'string' && e.error_code.trim()
              ? e.error_code.trim()
              : undefined;
            const orbitErrorKind = orbitErrorCode === 'ORBIT_CHAT_SERVICE_UNAVAILABLE'
              ? 'service_unavailable'
              : orbitErrorCode
                ? 'generic'
                : undefined;

            if (orbitErrorCode) {
              console.warn('[useOrbitChat] assistant error response', { orbitErrorCode });
            }

            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantPlaceholder.id
                  ? {
                    ...m,
                    content: errorText,
                    role: 'assistant',
                    metadata: orbitErrorCode
                      ? {
                        orbitErrorCode,
                        orbitErrorKind,
                      }
                      : undefined,
                  }
                  : m,
              ),
            );
          },

          onFinally: () => {
            setIsStreaming(false);
            setStreamingMessageId(null);
          },
        },
      );
    },
    [clientId, client, contextChips, contextUploads],
  );

  const resetConversation = useCallback(() => {
    abortRef.current?.abort();
    conversationIdRef.current = undefined;
    setConversationId(undefined);
    setMessages([]);
    setIsStreaming(false);
    setStreamingMessageId(null);
    setQuotaExceeded(false);
    setContextChips({});
    setContextUploads([]);
  }, []);

  const submitFeedback = useCallback(
    (payload: OrbitFeedbackPayload) => {
      // Fire-and-forget — do not await
      submitOrbitFeedback(clientId, payload).catch((err) =>
        console.warn('[useOrbitChat] feedback error', err),
      );
    },
    [clientId],
  );

  return {
    messages,
    isStreaming,
    streamingMessageId,
    quotaExceeded,
    conversationId,
    isLoadingHistory,
    contextChips,
    setContextChips,
    contextUploads,
    addContextUploads,
    removeContextUpload,
    sendMessage,
    resetConversation,
    submitFeedback,
  };
}
